import { useState } from "react";
import { Globe, Plus } from "lucide-react";
import { Button } from "@headerhawk/ui";
import { describeRolesAllowedTo } from "@headerhawk/core/roles";
import { isImpersonationTab } from "@/lib/auth/impersonation";
import { AddSiteDialog } from "@/components/settings/add-site-dialog";
import { useCurrentRole } from "@/lib/hooks/use-current-role";
import { useAuthActions } from "./auth-context";

/**
 * What someone who joined an existing tenant sees when it has no sites.
 *
 * `RequireAuth` shows this instead of onboarding to anyone who cannot name the
 * organization (see the comment there). A role that can add a site gets the
 * dialog the Sites page uses; anyone else is told who can, because until
 * someone does there is nothing on the dashboard to show them.
 */
export function NoSitesYet({ onSiteAdded }: { onSiteAdded: () => void }) {
  const { can } = useCurrentRole();
  const { logout } = useAuthActions();
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const canAddSite = can("POST /sites");

  return (
    <main className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
        <Globe className="h-6 w-6 text-muted-foreground" />
      </div>
      <h1 className="text-2xl font-semibold">No sites yet</h1>
      <p className="max-w-md text-sm text-muted-foreground">
        Your organization is not monitoring any sites, so there are no reports
        to show.{" "}
        {canAddSite
          ? "Add one to start collecting CSP violation reports."
          : isImpersonationTab()
            ? "This is a read-only view, so no site can be added from it."
            : `Only ${describeRolesAllowedTo("POST /sites")} can add a site, so ask one of them to.`}
      </p>
      {canAddSite && (
        <Button onClick={() => setAddDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Add Site
        </Button>
      )}
      <Button variant="ghost" onClick={() => void logout()}>
        Sign out
      </Button>
      {canAddSite && (
        <AddSiteDialog
          open={addDialogOpen}
          onOpenChange={setAddDialogOpen}
          onSuccess={() => {
            setAddDialogOpen(false);
            onSiteAdded();
          }}
        />
      )}
    </main>
  );
}
